import React, { useEffect, useMemo, useState } from "react";
import { apiFetch } from "../api";
import DashboardKpiCard, { DashboardKpiRow } from "./DashboardKpiCard";

import { useI18n } from "../i18n/I18nContext";
import { PLIS_FILTER_OPTIONS } from "../utils/empilementPopulations";

const STADE_COLORS = {
  banc: "#4C9A2A",
  cond: "#0F9D9D",
  imp: "#E8743B",
  sech: "#7B5EA7",
};

const STADE_LABELS = {
  banc: "Banc de test (H Merlin brut)",
  cond: "Après conditionnement",
  imp: "Après imprégnation",
  sech: "Après séchage",
};

const GRAN_LABELS = { jour: "Jour", semaine: "Semaine", mois: "Mois" };
const GRAN_FIELD = { jour: "lj", semaine: "ls", mois: "lm" };

const MONTHS_FR = [
  "Jan", "Fév", "Mar", "Avr", "Mai", "Juin",
  "Juil", "Août", "Sep", "Oct", "Nov", "Déc",
];

const COLORS = {
  yellow: "#E6FF3E",
  gray: "#94a3b8",
  red: "#f87171",
  white: "#e2e8f0",
  grid: "#2a4a62",
};

export function formatHumiditePeriod(period, gran) {
  if (!period) return "—";
  if (gran === "jour") {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(period);
    if (m) return `${m[3]}/${m[2]}`;
  }
  if (gran === "semaine") {
    const m = /^(\d{4})-S(\d{2})$/.exec(period);
    if (m) return `S${m[2]}`;
  }
  if (gran === "mois") {
    const m = /^(\d{4})-(\d{2})$/.exec(period);
    if (m) {
      const mi = parseInt(m[2], 10) - 1;
      return `${MONTHS_FR[mi] ?? m[2]} ${m[1]}`;
    }
  }
  return period;
}

function quantile(sorted, p) {
  if (!sorted.length) return 0;
  const pos = (sorted.length - 1) * p;
  const base = Math.floor(pos);
  const rest = pos - base;
  if (sorted[base + 1] !== undefined) {
    return sorted[base] + rest * (sorted[base + 1] - sorted[base]);
  }
  return sorted[base];
}

function boxStats(points) {
  const values = points.map((p) => (typeof p === "number" ? p : p.v));
  if (!values.length) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const q1 = quantile(sorted, 0.25);
  const median = quantile(sorted, 0.5);
  const q3 = quantile(sorted, 0.75);
  const iqr = q3 - q1;
  const whiskerLow = sorted.find((v) => v >= q1 - 1.5 * iqr) ?? sorted[0];
  const whiskerHigh = [...sorted].reverse().find((v) => v <= q3 + 1.5 * iqr) ?? sorted[sorted.length - 1];
  const mean = sorted.reduce((a, b) => a + b, 0) / sorted.length;
  return {
    q1, median, q3, whiskerLow, whiskerHigh, mean,
    min: sorted[0],
    max: sorted[sorted.length - 1],
    points: sorted,
    rawPoints: points,
  };
}

function buildSeriesFromRecords(records, gran, popMode) {
  const field = GRAN_FIELD[gran];
  const labelSet = new Set();
  const buckets = { banc: {}, cond: {}, imp: {}, sech: {} };

  for (const r of records || []) {
    if (popMode !== "all" && String(r.p) !== popMode) continue;
    const lab = r[field];
    if (!lab) continue;
    labelSet.add(lab);
    if (!buckets[r.s][lab]) buckets[r.s][lab] = [];
    buckets[r.s][lab].push({
      v: r.v,
      stack: r.stack,
      pos: r.pos,
      plyPn: r.plyPn,
      p: r.p,
    });
  }

  const periods = Array.from(labelSet).sort();
  return { periods, buckets };
}

function getOutliers(rawPoints, stats) {
  if (!stats || !rawPoints?.length) return [];
  return rawPoints.filter(
    (p) => p.v < stats.whiskerLow || p.v > stats.whiskerHigh
  );
}

function BoxTooltip({ tip }) {
  if (!tip) return null;
  const { stats, period, stade, gran, x, y } = tip;
  const rows = stats.rawPoints || [];

  return (
    <div
      style={{
        position: "fixed",
        left: Math.min(x + 14, window.innerWidth - 320),
        top: Math.min(y + 14, window.innerHeight - 280),
        zIndex: 9999,
        background: "#071e2f",
        border: `1px solid ${STADE_COLORS[stade]}`,
        borderRadius: 10,
        padding: "12px 14px",
        minWidth: 240,
        maxWidth: 300,
        pointerEvents: "none",
        boxShadow: "0 8px 32px rgba(0,0,0,0.45)",
        fontSize: 12,
        lineHeight: 1.5,
        color: "#e2e8f0",
      }}
    >
      <div style={{ color: STADE_COLORS[stade], fontWeight: 800, marginBottom: 6 }}>
        {STADE_LABELS[stade]}
      </div>
      <div style={{ color: COLORS.gray, marginBottom: 8 }}>
        {formatHumiditePeriod(period, gran)}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "2px 12px" }}>
        <span style={{ color: COLORS.gray }}>n</span><strong>{rows.length}</strong>
        <span style={{ color: COLORS.gray }}>Min</span><strong>{stats.min?.toFixed(2)} %</strong>
        <span style={{ color: COLORS.gray }}>Q1</span><strong>{stats.q1?.toFixed(2)} %</strong>
        <span style={{ color: COLORS.gray }}>Médiane</span><strong>{stats.median?.toFixed(2)} %</strong>
        <span style={{ color: COLORS.gray }}>Q3</span><strong>{stats.q3?.toFixed(2)} %</strong>
        <span style={{ color: COLORS.gray }}>Max</span><strong>{stats.max?.toFixed(2)} %</strong>
        <span style={{ color: COLORS.gray }}>Moyenne</span><strong>{stats.mean?.toFixed(2)} %</strong>
      </div>
    </div>
  );
}

function GroupedBoxplotSvg({ periods, buckets, gran, width, height }) {
  const stades = ["banc", "cond", "imp", "sech"];
  const margin = { top: 36, right: 28, bottom: 110, left: 72 };
  const innerW = width - margin.left - margin.right;
  const innerH = height - margin.top - margin.bottom;
  const [tooltip, setTooltip] = useState(null);

  const boxes = useMemo(() => {
    return periods.flatMap((period) =>
      stades.map((stade) => {
        const rawPoints = buckets[stade]?.[period] || [];
        return {
          period,
          stade,
          label: formatHumiditePeriod(period, gran),
          stats: boxStats(rawPoints),
          n: rawPoints.length,
        };
      })
    );
  }, [periods, buckets, gran]);

  const allY = boxes.flatMap((b) => b.stats?.points || []);
  const yMin = allY.length ? Math.min(...allY) : 0;
  const yMax = allY.length ? Math.max(...allY) : 20;
  const pad = (yMax - yMin) * 0.12 || 1;
  const domainMin = Math.max(0, yMin - pad);
  const domainMax = yMax + pad;

  const yScale = (v) =>
    margin.top + innerH - ((v - domainMin) / (domainMax - domainMin)) * innerH;

  const groupCount = Math.max(periods.length, 1);
  const groupW = innerW / groupCount;
  const boxW = Math.min(22, groupW / 5.5);

  const yTicks = 6;
  const tickValues = Array.from({ length: yTicks + 1 }, (_, i) =>
    domainMin + ((domainMax - domainMin) * i) / yTicks
  );

  const showTip = (e, b) => {
    if (!b?.stats) return;
    setTooltip({
      x: e.clientX,
      y: e.clientY,
      period: b.period,
      stade: b.stade,
      gran,
      stats: b.stats,
    });
  };

  return (
    <>
    <svg
      width={width}
      height={height}
      role="img"
      aria-label="Boxplot humidité des plis"
      onMouseLeave={() => setTooltip(null)}
    >
      {tickValues.map((v) => (
        <g key={v}>
          <line
            x1={margin.left}
            x2={margin.left + innerW}
            y1={yScale(v)}
            y2={yScale(v)}
            stroke={COLORS.grid}
            strokeWidth={1}
            strokeDasharray="4 4"
          />
          <text
            x={margin.left - 10}
            y={yScale(v) + 5}
            textAnchor="end"
            fill={COLORS.white}
            fontSize={12}
            fontWeight={600}
          >
            {v.toFixed(1)} %
          </text>
        </g>
      ))}

      {periods.map((period, gi) => {
        const gx = margin.left + gi * groupW + groupW / 2;
        return (
          <g key={period}>
            <text
              x={gx}
              y={height - margin.bottom + 28}
              textAnchor="middle"
              fill={COLORS.gray}
              fontSize={11}
              fontWeight={600}
            >
              {formatHumiditePeriod(period, gran)}
            </text>
            {stades.map((stade, si) => {
              const b = boxes.find((x) => x.period === period && x.stade === stade);
              if (!b?.stats) return null;
              const cx = gx + (si - 1.5) * (boxW + 5);
              const { q1, median, q3, whiskerLow, whiskerHigh } = b.stats;
              const color = STADE_COLORS[stade];
              const outliers = getOutliers(b.stats.rawPoints, b.stats);
              const hitTop = yScale(whiskerHigh) - 8;
              const hitH = Math.max(20, yScale(whiskerLow) - yScale(whiskerHigh) + 16);
              return (
                <g key={`${period}-${stade}`}>
                  <rect
                    x={cx - boxW / 2 - 4}
                    y={hitTop}
                    width={boxW + 8}
                    height={hitH}
                    fill="transparent"
                    style={{ cursor: "pointer" }}
                    onMouseEnter={(e) => showTip(e, b)}
                    onMouseMove={(e) => showTip(e, b)}
                  />
                  <line
                    x1={cx}
                    x2={cx}
                    y1={yScale(whiskerLow)}
                    y2={yScale(q1)}
                    stroke={color}
                    strokeWidth={1.5}
                  />
                  <line
                    x1={cx}
                    x2={cx}
                    y1={yScale(q3)}
                    y2={yScale(whiskerHigh)}
                    stroke={color}
                    strokeWidth={1.5}
                  />
                  <line
                    x1={cx - boxW / 2}
                    x2={cx + boxW / 2}
                    y1={yScale(whiskerLow)}
                    y2={yScale(whiskerLow)}
                    stroke={color}
                    strokeWidth={1.5}
                  />
                  <line
                    x1={cx - boxW / 2}
                    x2={cx + boxW / 2}
                    y1={yScale(whiskerHigh)}
                    y2={yScale(whiskerHigh)}
                    stroke={color}
                    strokeWidth={1.5}
                  />
                  <rect
                    x={cx - boxW / 2}
                    y={yScale(q3)}
                    width={boxW}
                    height={Math.max(2, yScale(q1) - yScale(q3))}
                    fill={color}
                    fillOpacity={0.35}
                    stroke={color}
                    strokeWidth={1.5}
                  />
                  <line
                    x1={cx - boxW / 2}
                    x2={cx + boxW / 2}
                    y1={yScale(median)}
                    y2={yScale(median)}
                    stroke={color}
                    strokeWidth={2.5}
                  />
                  {outliers.map((pt, idx) => (
                    <circle
                      key={`${pt.stack}-${pt.pos}-${idx}`}
                      cx={cx + ((idx % 3) - 1) * 5}
                      cy={yScale(pt.v)}
                      r={4}
                      fill={COLORS.red}
                      fillOpacity={0.9}
                      stroke="#fff"
                      strokeWidth={1}
                      style={{ cursor: "pointer" }}
                      onMouseEnter={(e) => showTip(e, {
                        ...b,
                        stats: {
                          ...b.stats,
                          rawPoints: [pt],
                          min: pt.v, max: pt.v, q1: pt.v, median: pt.v, q3: pt.v, mean: pt.v,
                        },
                      })}
                      onMouseMove={(e) => showTip(e, {
                        ...b,
                        stats: {
                          ...b.stats,
                          rawPoints: [pt],
                          min: pt.v, max: pt.v, q1: pt.v, median: pt.v, q3: pt.v, mean: pt.v,
                        },
                      })}
                    />
                  ))}
                </g>
              );
            })}
          </g>
        );
      })}

      <text
        x={margin.left - 48}
        y={margin.top + innerH / 2}
        textAnchor="middle"
        fill={COLORS.gray}
        fontSize={11}
        fontWeight={700}
        transform={`rotate(-90 ${margin.left - 48} ${margin.top + innerH / 2})`}
      >
        Humidité (%)
      </text>

      <g transform={`translate(${margin.left + innerW - 210}, ${margin.top})`}>
        {stades.map((stade, i) => (
          <g key={stade} transform={`translate(0, ${i * 22})`}>
            <rect x={0} y={0} width={14} height={14} fill={STADE_COLORS[stade]} rx={2} />
            <text x={20} y={12} fill={COLORS.white} fontSize={10}>
              {STADE_LABELS[stade]}
            </text>
          </g>
        ))}
      </g>
    </svg>
    <BoxTooltip tip={tooltip} />
    </>
  );
}

export default function HumiditePlisBoxplot({ active = true, payload: payloadProp = null }) {
  const { t, ui } = useI18n();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [payload, setPayload] = useState(payloadProp);
  const [loaded, setLoaded] = useState(!!payloadProp);
  const [gran, setGran] = useState("mois");
  const [popMode, setPopMode] = useState("all");
  const [chartWidth, setChartWidth] = useState(900);

  useEffect(() => {
    if (payloadProp) {
      setPayload(payloadProp);
      setLoaded(true);
    }
  }, [payloadProp]);

  useEffect(() => {
    if (!active || loaded || payloadProp) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const res = await apiFetch("/api/dashboard/humidite-plis");
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        if (json.status === "error") throw new Error(json.message || "Erreur API");
        if (!cancelled) {
          setPayload(json);
          setLoaded(true);
        }
      } catch (e) {
        if (!cancelled) setError(e.message || "Erreur chargement humidité plis");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [active, loaded, payloadProp]);

  useEffect(() => {
    const onResize = () => {
      const el = document.getElementById("humidite-plis-chart-wrap");
      if (el) setChartWidth(Math.max(400, el.clientWidth));
    };
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [active, loaded]);

  const { periods, buckets, counts } = useMemo(() => {
    const { periods: p, buckets: b } = buildSeriesFromRecords(
      payload?.records,
      gran,
      popMode
    );
    const c = {
      banc: Object.values(b.banc || {}).flat().length,
      cond: Object.values(b.cond || {}).flat().length,
      imp: Object.values(b.imp || {}).flat().length,
      sech: Object.values(b.sech || {}).flat().length,
    };
    return { periods: p, buckets: b, counts: c };
  }, [payload, gran, popMode]);

  const funnel = payload?.funnel;
  const totalPoints = counts.banc + counts.cond + counts.imp + counts.sech;

  if (!active) return null;

  return (
    <>
      {loading && (
        <div style={{ color: COLORS.yellow, padding: 20, textAlign: "center" }}>
          Chargement humidité des plis…
        </div>
      )}
      {error && (
        <div style={{ color: COLORS.red, padding: 20, textAlign: "center" }}>{error}</div>
      )}

      {loaded && !error && (
        <>
          {!payloadProp && (
            <DashboardKpiRow>
              <DashboardKpiCard
                label="Stacks traités"
                value={funnel?.stacks_traites ?? 0}
                sub={`univers ${funnel?.stacks_univers ?? 0}`}
                color="#0F9D9D"
              />
              <DashboardKpiCard
                label="Plis examinés"
                value={funnel?.plis_total ?? 0}
                sub="tous stades"
                color="#60a5fa"
              />
              <DashboardKpiCard
                label="Périodes"
                value={periods.length}
                sub={GRAN_LABELS[gran]}
                color="#a78bfa"
              />
            </DashboardKpiRow>
          )}

          <div style={S.card}>
            <div style={S.headerRow}>
              <div>
                <div style={S.cardTitle}>Boxplot — humidité des plis</div>
                <div style={{ color: COLORS.gray, fontSize: 12, marginTop: 4 }}>
                  Banc de test · conditionnement · imprégnation · séchage — seuils valides : {payload?.thresholds?.min ?? 1} % ≤ H ≤ {payload?.thresholds?.max ?? 30} %
                </div>
              </div>
              <div style={S.filters}>
                <label style={S.filterLabel}>
                  Période
                  <select style={S.select} value={gran} onChange={(e) => setGran(e.target.value)}>
                    <option value="mois">Mois</option>
                    <option value="semaine">Semaine</option>
                    <option value="jour">Jour</option>
                  </select>
                </label>
                <label style={S.filterLabel}>
                  Population
                  <select style={S.select} value={popMode} onChange={(e) => setPopMode(e.target.value)}>
                    <option value="all">Toutes les populations</option>
                    {PLIS_FILTER_OPTIONS.map((o) => (
                      <option key={o.id} value={o.id}>{o.label}</option>
                    ))}
                  </select>
                </label>
              </div>
            </div>

            <div id="humidite-plis-chart-wrap" style={{ width: "100%", overflowX: "auto", marginTop: 4 }}>
              {periods.length === 0 || totalPoints === 0 ? (
                <div style={{ color: COLORS.gray, padding: 32, textAlign: "center" }}>
                  Aucune donnée pour cette sélection.
                </div>
              ) : (
                <GroupedBoxplotSvg
                  periods={periods}
                  buckets={buckets}
                  gran={gran}
                  width={Math.max(chartWidth, periods.length * 140)}
                  height={540}
                />
              )}
            </div>
          </div>
        </>
      )}
    </>
  );
}

const S = {
  card: {
    background: "rgba(255,255,255,0.03)",
    border: "1px solid #1e3a50",
    borderRadius: 14,
    padding: "20px 22px",
    marginBottom: 16,
  },
  headerRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 16,
    flexWrap: "wrap",
    marginBottom: 12,
  },
  cardTitle: {
    fontSize: 13,
    fontWeight: 700,
    color: "#94a3b8",
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    marginBottom: 4,
  },
  filters: {
    display: "flex",
    gap: 12,
    flexWrap: "wrap",
  },
  filterLabel: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
    fontSize: 10,
    fontWeight: 700,
    color: "#94a3b8",
    textTransform: "uppercase",
    letterSpacing: "0.06em",
  },
  select: {
    padding: "8px 12px",
    borderRadius: 8,
    border: "1px solid #1e3a50",
    background: "#082131",
    color: "#e2e8f0",
    fontSize: 13,
    outline: "none",
    minWidth: 180,
  },
};
